import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Slim floating radio bar.
 *
 * - Always visible under the header (top-left of the viewport).
 * - Never covers hero content — max height ~44px collapsed, ~64px expanded.
 * - 3 stations: Orgánica Ibiza · Global · Sónica.
 * - Autoplay "organica" on load. Browsers may block until first user
 *   interaction; the bar shows a subtle blinking dot until it starts.
 * - Streams are proxied through /api/radio/{id} because the upstream URLs
 *   are plain HTTP and would be blocked by the browser as mixed content.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATIONS = [
  { id: "global", short: "Ibiza Global" },
];

function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 translate-x-[1px]">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
    </svg>
  );
}
function IconVolume() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function RadioPlayer({ lang = "es" }) {
  const audioRef = useRef(null);
  const [station, setStation] = useState("global");
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const [showVolume, setShowVolume] = useState(false);
  const [hidden, setHidden] = useState(false);

  const streamUrl = useMemo(
    () => (station && API_URL ? `${API_URL}/api/radio/${station}` : ""),
    [station]
  );

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !streamUrl) return;
    setError(false);
    setLoading(true);
    el.src = streamUrl + `?t=${Date.now()}`;
    el.load();
    const attempt = el.play();
    if (attempt && typeof attempt.then === "function") {
      attempt
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [streamUrl]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      setLoading(true);
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => setPlaying(true))
          .catch(() => setError(true))
          .finally(() => setLoading(false));
      }
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  if (hidden) return null;

  const labels =
    lang === "es"
      ? { hide: "Ocultar radio", nowPlaying: "En directo" }
      : { hide: "Hide radio", nowPlaying: "Live" };

  return (
    <div
      data-testid="radio-player"
      className="fixed z-40 top-[76px] md:top-[80px] left-3 md:left-6"
    >
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => { setLoading(false); setError(false); }}
        onError={() => { setError(true); setLoading(false); setPlaying(false); }}
      />

      <div
        data-testid="radio-bar"
        className="flex items-center gap-2 h-9 md:h-10 pl-1 pr-2 rounded-full bg-[#070B19]/85 backdrop-blur-md border border-[#C9A961]/30 shadow-[0_6px_20px_rgba(0,0,0,0.35)]"
      >
        {/* Play toggle */}
        <button
          type="button"
          onClick={togglePlay}
          data-testid="radio-play-toggle"
          aria-label={playing ? "Pause" : "Play"}
          className={`shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors ${
            playing
              ? "bg-[#C9A961] text-[#070B19]"
              : "bg-white/10 text-[#C9A961] border border-[#C9A961]/40 hover:bg-[#C9A961]/20"
          }`}
        >
          {loading ? (
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : playing ? (
            <IconPause />
          ) : (
            <IconPlay />
          )}
        </button>

        {/* LIVE dot + label */}
        <div className="hidden md:flex items-center gap-1.5 pl-1 pr-2 border-r border-[#C9A961]/15">
          <span className="relative flex items-center justify-center w-2 h-2">
            {playing && (
              <span className="absolute inline-flex w-2 h-2 rounded-full bg-[#C9A961] animate-ping opacity-70" />
            )}
            <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${playing ? "bg-[#C9A961]" : "bg-white/40"}`} />
          </span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[#C9A961]">
            {labels.nowPlaying}
          </span>
        </div>

        {/* Station pills */}
        <div className="flex items-center gap-1" data-testid="radio-stations">
          {STATIONS.map((s) => {
            const active = station === s.id;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => setStation(s.id)}
                data-testid={`radio-station-${s.id}`}
                className={`text-[10px] md:text-[11px] px-2 py-1 rounded-full transition-colors ${
                  active
                    ? "bg-[#C9A961]/20 text-white border border-[#C9A961]/70"
                    : "text-white/60 hover:text-white border border-transparent"
                }`}
              >
                {s.short}
              </button>
            );
          })}
        </div>

        {/* Volume button + slider popover */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowVolume((v) => !v)}
            data-testid="radio-volume-toggle"
            aria-label="Volume"
            className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-[#C9A961]"
          >
            <IconVolume />
          </button>
          {showVolume && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-[#070B19]/95 border border-[#C9A961]/30 rounded-full px-3 py-2">
              <input
                data-testid="radio-volume"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                aria-label="Volume"
                className="w-24 accent-[#C9A961]"
              />
            </div>
          )}
        </div>

        {/* Hide */}
        <button
          type="button"
          onClick={() => setHidden(true)}
          data-testid="radio-hide"
          aria-label={labels.hide}
          className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-[#C9A961]"
        >
          <IconClose />
        </button>
      </div>

      {error && (
        <div
          data-testid="radio-error"
          className="mt-1.5 text-[10px] text-red-300/80 bg-red-300/5 border border-red-300/20 rounded-full px-3 py-1 max-w-[240px]"
        >
          {lang === "es"
            ? "Emisora no disponible ahora"
            : "Station unavailable right now"}
        </div>
      )}
    </div>
  );
}
